import type { VoidFunctionComponent } from 'react';

import { useReportLogoSettings } from '../../../../hooks/api/organization/useReportLogoSettings';
import { FormGroup, LabeledSelect } from '../../../generic/Forms';
import LogoUpload from '../../../LogoUpload';
import Header from '../../../shared/settings/SettingsPageHeader';
import Paragraph from '../../../shared/settings/SettingsPageParagraph';
import Section from '../../../shared/settings/SettingsPageSection';
import PageContent from '../shared/SettingsPageContent';
import type { SelectOption } from '../../../../types';

import { useProjectReportsSettings } from './useProjectReportsSettings';

const ProjectReportsSettingsContainer: VoidFunctionComponent = () => {
  const {
    fileUrl,
    setFile,
    unsetFile,
    handleImgError,
    onSave: onSaveOrganizationLogo,
    isSaveDisabled: isOrganizationLogoSaveDisabled,
    isLoading: isOrganizationLogoLoading,
  } = useReportLogoSettings();

  const {
    resolvedSettings,
    reportOptionSelectOptions,
    isLoading,
    isSaving,
    isSaveButtonDisabled,
    canEdit,
    onUpdate,
    onSave: onSaveProjectReports,
  } = useProjectReportsSettings();

  const isAnySaving = isOrganizationLogoLoading || isSaving;
  const isSaveButtonDisabledCombined = isOrganizationLogoSaveDisabled && isSaveButtonDisabled;

  const onSaveAll = async () => {
    if (!isOrganizationLogoSaveDisabled) {
      await onSaveOrganizationLogo();
    }
    if (!isSaveButtonDisabled) {
      await onSaveProjectReports();
    }
  };

  return (
    <PageContent isLoading={isLoading}>
      <Header heading="Reports">
        <Header.SaveButton isLoading={isAnySaving} isDisabled={isSaveButtonDisabledCombined} onClick={onSaveAll} />
      </Header>
      <Section>
        <Section.Left heading="Logo">
          <Section.Paragraph>
            Upload the logo that will appear on the cover and header of your reports. This is a global setting and
            will be applied to all projects and release groups in your organization.
          </Section.Paragraph>
        </Section.Left>
        <Section.Right>
          <LogoUpload fileUrl={fileUrl} setFile={setFile} unsetFile={unsetFile} handleImgError={handleImgError} />
        </Section.Right>
      </Section>
      <Section>
        <Section.Left heading="Saved Options">
          <Section.Paragraph>Choose default options for each report type.</Section.Paragraph>
        </Section.Left>
        <Section.Right>
          <FormGroup>
            <div>
              <LabeledSelect<SelectOption<number>>
                label="Default options for Licensing reports"
                id="project-settings-default-saved-option-licensing-report"
                placeholder="No filter selected"
                onChange={(option) =>
                  onUpdate({
                    defaultSavedOptionLicensingReport: option?.value ?? null,
                  })}
                options={reportOptionSelectOptions}
                value={resolvedSettings.defaultSavedOptionLicensingReport}
                isDisabled={!canEdit || isAnySaving}
                isClearable
              />
              <Paragraph>This filter will be active by default when a user visits the report</Paragraph>
            </div>
            <div>
              <LabeledSelect<SelectOption<number>>
                label="Default options for SBOM reports"
                id="project-settings-default-saved-option-sbom"
                placeholder="No filter selected"
                onChange={(option) =>
                  onUpdate({
                    defaultSavedOptionSbom: option?.value ?? null,
                  })}
                options={reportOptionSelectOptions}
                value={resolvedSettings.defaultSavedOptionSbom}
                isDisabled={!canEdit || isAnySaving}
                isClearable
              />
              <Paragraph>This filter will be active by default when a user visits the report</Paragraph>
            </div>
          </FormGroup>
        </Section.Right>
      </Section>
    </PageContent>
  );
};

export default ProjectReportsSettingsContainer;
